// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

/**
 * Address Information
 */
export const zAddressInfo = z.object({
    addressLineOne: z.string().min(1).max(255),
    addressLineTwo: z.string().min(1).max(150).optional(),
    city: z.string().min(1).max(150),
    country: z.string().length(2),
    stateProvinceRegion: z.string().min(1).max(150),
    zipPostalCode: z.string().min(1).max(150)
});

/**
 * Values that can be used to suggest addresses
 */
export const zAddressSuggestPostBody = z.object({
    addressLineOne: z.string().max(255).nullish(),
    addressLineTwo: z.string().max(150).nullish(),
    city: z.string().max(150).nullish(),
    country: z.string().max(2).nullish(),
    latitude: z.number().gte(-90).lte(90).nullish(),
    longitude: z.number().gte(-180).lte(180).nullish(),
    name: z.string().nullish(),
    stateProvinceRegion: z.string().max(150).nullish(),
    zipPostalCode: z.string().max(150).nullish()
});

/**
 * Address suggestion item
 */
export const zAddressSuggestionItem = z.object({
    addressLineOne: z.string().nullish(),
    addressLineTwo: z.string().nullish(),
    city: z.string().nullish(),
    country: z.string().nullish(),
    name: z.string().nullish(),
    stateProvinceRegion: z.string().nullish(),
    zipPostalCode: z.string().nullish()
});

/**
 * A list of autocomplete addresses
 */
export const zAddressSuggestionListBody = z.object({
    items: z.array(zAddressSuggestionItem)
});

/**
 * Change ownership request status
 */
export const zChangeOwnershipRequestStatusEnum = z.enum([
    'pending',
    'accepted',
    'done'
]);

/**
 * string of at most 255 characters
 */
export const zCharacterString255 = z.string().max(255);

/**
 * Business partner change request properties
 */
export const zBusinessPartnerChangeBody = z.object({
    companyAddress: zAddressInfo,
    name: zCharacterString255
});

/**
 * string of at most 64 characters
 */
export const zCharacterString64 = z.string().max(64);

export const zCharacterStringEmail = z.string().email().min(6).max(255).regex(/^\S+@\S+\.\S+$/);

/**
 * Change ownership post body
 */
export const zChangeOwnershipRequestPostBody = z.object({
    requestedOwnerEmail: zCharacterStringEmail
});

export const zCharacterStringType = z.string().min(1).max(32).regex(/^[0-9A-Za-z_]+$/);

export const zCharacterStringUrn = z.string().min(10).max(255).regex(/^urn:.*/);

/**
 * a Universally Unique IDentifier in conformance with IETF RFC 4122
 */
export const zCharacterStringUuid = z.string().max(36).regex(/^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/);

/**
 * Business partner Properties
 */
export const zBusinessPartnerBody = z.object({
    companyAddress: zAddressInfo,
    createdAt: z.string().datetime(),
    extId: zCharacterStringUrn,
    id: zCharacterStringUuid,
    modifiedAt: z.string().datetime().optional(),
    name: zCharacterString255,
    ownerId: zCharacterStringUuid.optional(),
    parentId: zCharacterStringUuid.optional(),
    type: zCharacterStringType
});

/**
 * A list of businessPartnerBody
 */
export const zBusinessPartnerListBody = z.object({
    items: z.array(zBusinessPartnerBody)
});

/**
 * Change ownership accept body
 */
export const zChangeOwnershipRequestAcceptBody = z.object({
    businessPartner: zBusinessPartnerBody,
    id: zCharacterStringUuid,
    status: zChangeOwnershipRequestStatusEnum
});

/**
 * Change ownership body
 */
export const zChangeOwnershipRequestBody = z.object({
    id: zCharacterStringUuid,
    status: zChangeOwnershipRequestStatusEnum
});

/**
 * ISO 4217 currency code
 */
export const zCurrencyCode = z.string().length(3);

/**
 * Customer
 */
export const zCustomerBody = z.object({
    companyAddress: zAddressInfo,
    id: zCharacterStringUuid,
    name: zCharacterString255,
    phone: zCharacterString255
});

/**
 * A list of customers
 */
export const zCustomerListBody = z.object({
    items: z.array(zCustomerBody)
});

/**
 * Request body for the migration candidate accept endpoint. externalId identifies the target Business Partner by ext_id (Topcon Resource Name, e.g. urn:topcon:md::000000000001:bp).
 */
export const zMigrationCandidateAcceptPostBody = z.object({
    externalId: z.string()
});

/**
 * A candidate match target considered by the source system's matching algorithm, with its per-dimension score breakdown.
 */
export const zMigrationCandidateMatchTarget = z.object({
    address_score: z.number(),
    business_address_city: z.string().nullish(),
    business_address_country_code: z.string().nullish(),
    business_address_line_1: z.string().nullish(),
    business_address_state_province_region: z.string().nullish(),
    business_address_zip_postal_code: z.string().nullish(),
    domain_score: z.number(),
    external_id: z.string(),
    name: z.string(),
    name_score: z.number(),
    score: z.number()
});

/**
 * Match-quality metadata produced by a source system's matching algorithm. The known fields mirror the TLM enricher output (project/migration/tlm/ciamstore); because this is stored as a JSONB column and the shape may vary by source system, additional fields may be present.
 */
export const zMigrationCandidateMatchDetails = z.object({
    score: z.number(),
    score_gap_1_2: z.number().nullable(),
    top_targets: z.array(zMigrationCandidateMatchTarget)
});

/**
 * Migration candidate business partner properties
 */
export const zMigrationCandidateBusinessPartnerBody = z.object({
    address: zAddressInfo,
    businessPartner: zBusinessPartnerBody.optional(),
    createdAt: z.string().datetime(),
    dealerBusinessPartnerId: zCharacterStringUuid.optional(),
    id: zCharacterStringUuid,
    matchDetails: zMigrationCandidateMatchDetails.optional(),
    modifiedAt: z.string().datetime(),
    name: zCharacterString255,
    serviceAgreementStatus: z.enum([
        'pending',
        'accepted',
        'rejected',
        'matched'
    ]),
    sourceId: zCharacterString255,
    sourceSystem: zCharacterString64,
    status: z.enum([
        'pending',
        'accepted',
        'rejected',
        'matched'
    ])
});

/**
 * A list of migrationCandidateBusinessPartnerBody
 */
export const zMigrationCandidateBusinessPartnerListBody = z.object({
    items: z.array(zMigrationCandidateBusinessPartnerBody)
});

/**
 * Migration candidate user properties
 */
export const zMigrationCandidateUserBody = z.object({
    createdAt: z.string().datetime(),
    email: zCharacterStringEmail,
    id: zCharacterStringUuid,
    modifiedAt: z.string().datetime(),
    profiles: z.array(z.string()),
    status: z.enum([
        'pending',
        'accepted',
        'rejected'
    ])
});

/**
 * Pending migration candidate users whose business partner candidate is matched or accepted to the current business partner
 */
export const zMigrationCandidateUserListBody = z.object({
    items: z.array(zMigrationCandidateUserBody)
});

/**
 * A paginated list of businessPartnerBody
 */
export const zPaginatedBusinessPartnerListBody = z.object({
    items: z.array(zBusinessPartnerBody),
    limit: z.number().int(),
    page: z.number().int(),
    total: z.number().int()
});

/**
 * A paginated list of migrationCandidateBusinessPartnerBody
 */
export const zPaginatedMigrationCandidateBusinessPartnerListBody = z.object({
    items: z.array(zMigrationCandidateBusinessPartnerBody),
    limit: z.number().int(),
    page: z.number().int(),
    total: z.number().int()
});

/**
 * Product line
 */
export const zProductLineBody = z.object({
    id: zCharacterStringUuid.optional(),
    name: zCharacterString255.optional()
});

/**
 * A list of product lines
 */
export const zProductLineListBody = z.object({
    items: z.array(zProductLineBody)
});

/**
 * service provider
 */
export const zProviderBody = z.object({
    companyAddress: zAddressInfo,
    id: zCharacterStringUuid,
    name: zCharacterString255
});

/**
 * A paginated list of service providers
 */
export const zProviderListBody = z.object({
    items: z.array(zProviderBody),
    limit: z.number().int(),
    page: z.number().int(),
    total: z.number().int()
});

/**
 * Registration token response
 */
export const zRegistrationTokenBody = z.object({
    registrationToken: z.string()
});

/**
 * Business partner request decline reason
 */
export const zRequestBusinessPartnerDeclineReasonEnum = z.enum([
    'reason.none',
    'reason.other',
    'reason.customer.invalid_details',
    'reason.service_agreement.details_not_satisfactory'
]);

/**
 * Customer declines business partner request
 */
export const zRequestBusinessPartnerDeclinePostBody = z.object({
    declineReason: zRequestBusinessPartnerDeclineReasonEnum.optional()
});

/**
 * Business partner request end-customer type
 */
export const zRequestBusinessPartnerEndCustomerTypeEnum = z.enum(['end_customer_enterprise', 'end_customer_individual']);

/**
 * Business partner request patch properties
 */
export const zRequestBusinessPartnerPatchBody = z.object({
    companyAddress: zAddressInfo,
    name: z.string().min(1).max(80)
});

/**
 * Business partner request status
 */
export const zRequestBusinessPartnerStatusEnum = z.enum([
    'customer_review',
    'customer_rejected',
    'pending',
    'accepted',
    'rejected',
    'done',
    'closed'
]);

/**
 * A list of role
 */
export const zRoleListBody = z.object({
    items: z.array(z.string())
});

/**
 * Service agreement decline reason
 */
export const zServiceAgreementDeclineReasonEnum = z.enum([
    'reason.none',
    'reason.other',
    'reason.customer.invalid_details',
    'reason.service_agreement.details_not_satisfactory'
]);

/**
 * Service agreement invitee declines service agreement request
 */
export const zServiceAgreementDeclinePostBody = z.object({
    declineReason: zServiceAgreementDeclineReasonEnum.optional()
});

/**
 * Service agreement status
 */
export const zServiceAgreementStatusEnum = z.enum([
    'active',
    'declined',
    'invited',
    'pending'
]);

/**
 * Service agreement terms type
 */
export const zServiceAgreementTermsEnum = z.enum(['full_service', 'partial_service']);

/**
 * Request to create a business partner
 */
export const zRequestBusinessPartnerBody = z.object({
    companyAddress: zAddressInfo,
    createdAt: z.string().datetime(),
    currency: zCurrencyCode,
    expiresAt: z.string().datetime().optional(),
    id: zCharacterStringUuid,
    modifiedAt: z.string().datetime(),
    name: z.string().min(1).max(80),
    ownerIdentityId: zCharacterStringUuid,
    parentBusinessPartnerId: zCharacterStringUuid.optional(),
    provider: zProviderBody,
    providerBusinessPartnerId: zCharacterStringUuid,
    providerIdentityId: zCharacterStringUuid.optional(),
    serviceAgreementTerms: zServiceAgreementTermsEnum,
    status: zRequestBusinessPartnerStatusEnum,
    type: zCharacterStringType
});

/**
 * A paginated list of requestBusinessPartnerBody
 */
export const zPaginatedRequestBusinessPartnerListBody = z.object({
    items: z.array(zRequestBusinessPartnerBody),
    limit: z.number().int(),
    page: z.number().int(),
    total: z.number().int()
});

/**
 * Request to create an end-customer type of business partner division
 */
export const zRequestBusinessPartnerDivisionPostBody = z.object({
    companyAddress: zAddressInfo,
    currency: zCurrencyCode,
    name: z.string().min(1).max(80),
    providerBusinessPartnerId: zCharacterStringUuid,
    serviceAgreementTerms: zServiceAgreementTermsEnum,
    type: zRequestBusinessPartnerEndCustomerTypeEnum
});

/**
 * Request to create an end-customer type of business partner
 */
export const zRequestBusinessPartnerEndCustomerPostBody = z.object({
    companyAddress: zAddressInfo,
    currency: zCurrencyCode,
    migrationCandidateBusinessPartnerId: zCharacterStringUuid.optional(),
    name: z.string().min(1).max(80),
    providerBusinessPartnerId: zCharacterStringUuid,
    serviceAgreementTerms: zServiceAgreementTermsEnum,
    type: zRequestBusinessPartnerEndCustomerTypeEnum
});

/**
 * A list of requestBusinessPartnerBody
 */
export const zRequestBusinessPartnerListBody = z.object({
    items: z.array(zRequestBusinessPartnerBody)
});

/**
 * As a service provider, proxy a request to create an end-customer type of business partner
 */
export const zRequestBusinessPartnerProxyPostBody = z.object({
    companyAddress: zAddressInfo,
    currency: zCurrencyCode,
    migrationCandidateBusinessPartnerId: zCharacterStringUuid.optional(),
    name: z.string().min(1).max(80),
    ownerEmail: zCharacterStringEmail,
    providerCustomerNumber: zCharacterString64.optional(),
    serviceAgreementTerms: zServiceAgreementTermsEnum,
    type: zRequestBusinessPartnerEndCustomerTypeEnum
});

/**
 * Service agreement properties
 */
export const zServiceAgreementBody = z.object({
    activeSince: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    customer: zBusinessPartnerBody.optional(),
    customerBusinessPartnerId: zCharacterStringUuid,
    declineReason: zServiceAgreementDeclineReasonEnum.optional(),
    id: zCharacterStringUuid,
    modifiedAt: z.string().datetime(),
    provider: zBusinessPartnerBody.optional(),
    providerBusinessPartnerId: zCharacterStringUuid,
    providerCustomerNumber: zCharacterString64.optional(),
    requestExpiresAt: z.string().datetime().optional(),
    serviceAgreementStatus: zServiceAgreementStatusEnum,
    serviceAgreementTerms: zServiceAgreementTermsEnum
});

/**
 * A paginated list of serviceAgreementBody
 */
export const zPaginatedServiceAgreementListBody = z.object({
    items: z.array(zServiceAgreementBody),
    limit: z.number().int(),
    page: z.number().int(),
    total: z.number().int()
});

/**
 * A list of serviceAgreementBody
 */
export const zServiceAgreementListBody = z.object({
    items: z.array(zServiceAgreementBody)
});

/**
 * Customer or provider updates service agreement
 */
export const zServiceAgreementPatchBody = z.object({
    serviceAgreementTerms: zServiceAgreementTermsEnum
});

/**
 * Provider creates a service agreement with a customer
 */
export const zServiceAgreementWithCustomerPostBody = z.object({
    customerBusinessPartnerId: zCharacterStringUuid,
    customerNumber: zCharacterString64.optional(),
    serviceAgreementTerms: zServiceAgreementTermsEnum
});

/**
 * Customer creates a service agreement with a provider
 */
export const zServiceAgreementWithProviderPostBody = z.object({
    providerBusinessPartnerId: zCharacterStringUuid,
    serviceAgreementTerms: zServiceAgreementTermsEnum
});

/**
 * TODO
 */
export const zPostV1AddressSuggestBody = zAddressSuggestPostBody;

/**
 * Successful Response
 */
export const zPostV1AddressSuggestResponse = zAddressSuggestionListBody;

/**
 * Successful Response
 */
export const zGetV1BusinessPartnerCurrentResponse = zBusinessPartnerBody;

/**
 * No Content
 */
export const zDeleteV1BusinessPartnerCurrentChangeResponse = z.void();

/**
 * Successful Response
 */
export const zGetV1BusinessPartnerCurrentChangeResponse = zBusinessPartnerChangeBody;

/**
 * TODO
 */
export const zPutV1BusinessPartnerCurrentChangeBody = zBusinessPartnerChangeBody;

export const zPutV1BusinessPartnerCurrentChangeHeaders = z.object({
    'If-Match': z.string().optional()
});

/**
 * Successful Response
 */
export const zGetV1BusinessPartnerCurrentDivisionsResponse = zBusinessPartnerListBody;

export const zGetV1BusinessPartnerCurrentMigrationCandidateUserByIdPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zGetV1BusinessPartnerCurrentMigrationCandidateUserByIdResponse = zMigrationCandidateUserBody;

export const zPostV1BusinessPartnerCurrentMigrationCandidateUserByIdAcceptPath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zPostV1BusinessPartnerCurrentMigrationCandidateUserByIdAcceptResponse = z.void();

export const zPostV1BusinessPartnerCurrentMigrationCandidateUserByIdRejectPath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zPostV1BusinessPartnerCurrentMigrationCandidateUserByIdRejectResponse = z.void();

/**
 * Successful Response
 */
export const zGetV1BusinessPartnerCurrentMigrationCandidateUsersResponse = zMigrationCandidateUserListBody;

/**
 * No Content
 */
export const zDeleteV1BusinessPartnerCurrentRequestChangeOwnershipResponse = z.void();

/**
 * Successful Response
 */
export const zGetV1BusinessPartnerCurrentRequestChangeOwnershipResponse = zChangeOwnershipRequestBody;

/**
 * TODO
 */
export const zPostV1BusinessPartnerCurrentRequestChangeOwnershipBody = zChangeOwnershipRequestPostBody;

/**
 * Created
 */
export const zPostV1BusinessPartnerCurrentRequestChangeOwnershipResponse = zChangeOwnershipRequestBody;

export const zGetV1BusinessPartnersQuery = z.object({
    page: z.number().int().optional(),
    limit: z.number().int().optional(),
    'filter[id]': z.record(z.unknown()).optional(),
    'filter[extId]': z.record(z.unknown()).optional(),
    'filter[parentId]': z.record(z.unknown()).optional(),
    'filter[ownerId]': z.record(z.unknown()).optional(),
    'filter[type]': z.record(z.unknown()).optional(),
    'filter[createdAt]': z.record(z.unknown()).optional(),
    'filter[modifiedAt]': z.record(z.unknown()).optional(),
    'filter[name]': z.record(z.unknown()).optional(),
    'filter[companyAddress.addressLineOne]': z.record(z.unknown()).optional(),
    'filter[companyAddress.addressLineTwo]': z.record(z.unknown()).optional(),
    'filter[companyAddress.city]': z.record(z.unknown()).optional(),
    'filter[companyAddress.stateProvinceRegion]': z.record(z.unknown()).optional(),
    'filter[companyAddress.zipPostalCode]': z.record(z.unknown()).optional(),
    'filter[companyAddress.country]': z.record(z.unknown()).optional(),
    sort: z.record(z.unknown()).optional(),
    search: z.string().optional()
});

/**
 * Successful Response
 */
export const zGetV1BusinessPartnersResponse = zPaginatedBusinessPartnerListBody;

export const zGetV1CustomerByCustomerNumberPath = z.object({
    customerNumber: z.string()
});

/**
 * Successful Response
 */
export const zGetV1CustomerByCustomerNumberResponse = zCustomerBody;

export const zGetV1CustomersQuery = z.object({
    'filter[name]': z.record(z.unknown()).optional()
});

/**
 * Successful Response
 */
export const zGetV1CustomersResponse = zCustomerListBody;

export const zGetV1IdentityCurrentMigrationCandidateBusinessPartnerByIdPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zGetV1IdentityCurrentMigrationCandidateBusinessPartnerByIdResponse = zMigrationCandidateBusinessPartnerBody;

/**
 * TODO
 */
export const zPostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAcceptBody = zMigrationCandidateAcceptPostBody;

export const zPostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAcceptPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zPostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdAcceptResponse = zMigrationCandidateBusinessPartnerBody;

export const zPostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdRejectPath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zPostV1IdentityCurrentMigrationCandidateBusinessPartnerByIdRejectResponse = z.void();

/**
 * Successful Response
 */
export const zGetV1IdentityCurrentMigrationCandidateBusinessPartnersResponse = zMigrationCandidateBusinessPartnerListBody;

/**
 * TODO
 */
export const zPostV1IdentityCurrentRequestBusinessPartnerBody = zRequestBusinessPartnerEndCustomerPostBody;

export const zDeleteV1IdentityCurrentRequestBusinessPartnerByIdPath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zDeleteV1IdentityCurrentRequestBusinessPartnerByIdResponse = z.void();

export const zGetV1IdentityCurrentRequestBusinessPartnerByIdPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zGetV1IdentityCurrentRequestBusinessPartnerByIdResponse = zRequestBusinessPartnerBody;

/**
 * TODO
 */
export const zPutV1IdentityCurrentRequestBusinessPartnerByIdBody = zRequestBusinessPartnerPatchBody;

export const zPutV1IdentityCurrentRequestBusinessPartnerByIdHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPutV1IdentityCurrentRequestBusinessPartnerByIdPath = z.object({
    id: z.string()
});

export const zPutV1IdentityCurrentRequestBusinessPartnerByIdResponse = z.union([
    zRequestBusinessPartnerBody,
    z.unknown()
]);

/**
 * Successful Response
 */
export const zGetV1IdentityCurrentRequestBusinessPartnersResponse = zRequestBusinessPartnerListBody;

export const zGetV1MigrationCandidateBusinessPartnerByIdPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zGetV1MigrationCandidateBusinessPartnerByIdResponse = zMigrationCandidateBusinessPartnerBody;

/**
 * TODO
 */
export const zPostV1MigrationCandidateBusinessPartnerByIdAcceptBody = zMigrationCandidateAcceptPostBody;

export const zPostV1MigrationCandidateBusinessPartnerByIdAcceptPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zPostV1MigrationCandidateBusinessPartnerByIdAcceptResponse = zMigrationCandidateBusinessPartnerBody;

export const zPostV1MigrationCandidateBusinessPartnerByIdRejectPath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zPostV1MigrationCandidateBusinessPartnerByIdRejectResponse = z.void();

export const zGetV1MigrationCandidateBusinessPartnersQuery = z.object({
    page: z.number().int().optional(),
    limit: z.number().int().optional(),
    'filter[name]': z.record(z.unknown()).optional()
});

/**
 * Successful Response
 */
export const zGetV1MigrationCandidateBusinessPartnersResponse = zPaginatedMigrationCandidateBusinessPartnerListBody;

/**
 * Successful Response
 */
export const zGetV1ProductLinesResponse = zProductLineListBody;

export const zGetV1ProvidersQuery = z.object({
    'filter[name]': z.record(z.unknown()).optional(),
    'filter[companyAddress.addressLineOne]': z.record(z.unknown()).optional(),
    'filter[companyAddress.addressLineTwo]': z.record(z.unknown()).optional(),
    'filter[companyAddress.city]': z.record(z.unknown()).optional(),
    'filter[companyAddress.stateProvinceRegion]': z.record(z.unknown()).optional(),
    'filter[companyAddress.zipPostalCode]': z.record(z.unknown()).optional(),
    'filter[companyAddress.country]': z.record(z.unknown()).optional(),
    page: z.number().int().optional(),
    limit: z.number().int().optional()
});

/**
 * Successful Response
 */
export const zGetV1ProvidersResponse = zProviderListBody;

/**
 * TODO
 */
export const zPostV1RequestBusinessPartnerDivisionBody = zRequestBusinessPartnerDivisionPostBody;

/**
 * Successful Response
 */
export const zGetV1RequestBusinessPartnerDivisionsResponse = zRequestBusinessPartnerListBody;

export const zGetV1RequestBusinessPartnerProxiesQuery = z.object({
    page: z.number().int().optional(),
    limit: z.number().int().optional(),
    'filter[name]': z.record(z.unknown()).optional(),
    'filter[status]': z.record(z.unknown()).optional()
});

/**
 * Successful Response
 */
export const zGetV1RequestBusinessPartnerProxiesResponse = zPaginatedRequestBusinessPartnerListBody;

/**
 * TODO
 */
export const zPostV1RequestBusinessPartnerProxyBody = zRequestBusinessPartnerProxyPostBody;

export const zPostV1RequestBusinessPartnerProxyResponse = z.union([
    zRequestBusinessPartnerBody,
    z.unknown()
]);

export const zDeleteV1RequestBusinessPartnerByIdPath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zDeleteV1RequestBusinessPartnerByIdResponse = z.void();

export const zGetV1RequestBusinessPartnerByIdPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zGetV1RequestBusinessPartnerByIdResponse = zRequestBusinessPartnerBody;

/**
 * TODO
 */
export const zPutV1RequestBusinessPartnerByIdBody = zRequestBusinessPartnerPatchBody;

export const zPutV1RequestBusinessPartnerByIdHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPutV1RequestBusinessPartnerByIdPath = z.object({
    id: z.string()
});

export const zPutV1RequestBusinessPartnerByIdResponse = z.union([
    zRequestBusinessPartnerBody,
    z.unknown()
]);

export const zPostV1RequestBusinessPartnerByIdAcceptPath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zPostV1RequestBusinessPartnerByIdAcceptResponse = z.void();

/**
 * TODO
 */
export const zPostV1RequestBusinessPartnerByIdDeclineBody = zRequestBusinessPartnerDeclinePostBody;

export const zPostV1RequestBusinessPartnerByIdDeclinePath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zPostV1RequestBusinessPartnerByIdDeclineResponse = z.void();

export const zPostV1RequestBusinessPartnerByIdRegistrationTokenPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zPostV1RequestBusinessPartnerByIdRegistrationTokenResponse = zRegistrationTokenBody;

export const zGetV1RequestChangeOwnershipByIdPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zGetV1RequestChangeOwnershipByIdResponse = zChangeOwnershipRequestAcceptBody;

export const zPostV1RequestChangeOwnershipByIdAcceptHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPostV1RequestChangeOwnershipByIdAcceptPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zGetV1RolesCurrentResponse = zRoleListBody;

/**
 * TODO
 */
export const zPostV1ServiceAgreementWithCustomerBody = zServiceAgreementWithCustomerPostBody;

/**
 * Created
 */
export const zPostV1ServiceAgreementWithCustomerResponse = zServiceAgreementBody;

/**
 * TODO
 */
export const zPostV1ServiceAgreementWithProviderBody = zServiceAgreementWithProviderPostBody;

/**
 * Created
 */
export const zPostV1ServiceAgreementWithProviderResponse = zServiceAgreementBody;

export const zDeleteV1ServiceAgreementByIdPath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zDeleteV1ServiceAgreementByIdResponse = z.void();

export const zGetV1ServiceAgreementByIdPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zGetV1ServiceAgreementByIdResponse = zServiceAgreementBody;

/**
 * TODO
 */
export const zPutV1ServiceAgreementByIdBody = zServiceAgreementPatchBody;

export const zPutV1ServiceAgreementByIdHeaders = z.object({
    'If-Match': z.string().optional()
});

export const zPutV1ServiceAgreementByIdPath = z.object({
    id: z.string()
});

/**
 * Successful Response
 */
export const zPutV1ServiceAgreementByIdResponse = zServiceAgreementBody;

export const zPostV1ServiceAgreementByIdAcceptPath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zPostV1ServiceAgreementByIdAcceptResponse = z.void();

/**
 * TODO
 */
export const zPostV1ServiceAgreementByIdDeclineBody = zServiceAgreementDeclinePostBody;

export const zPostV1ServiceAgreementByIdDeclinePath = z.object({
    id: z.string()
});

/**
 * No Content
 */
export const zPostV1ServiceAgreementByIdDeclineResponse = z.void();

export const zGetV1ServiceAgreementsWithCustomerQuery = z.object({
    page: z.number().int().optional(),
    limit: z.number().int().optional(),
    'filter[providerBusinessPartnerId]': z.record(z.unknown()).optional(),
    'filter[customer.name]': z.record(z.unknown()).optional(),
    'filter[serviceAgreementStatus]': z.record(z.unknown()).optional(),
    scope: z.string().optional()
});

/**
 * Successful Response
 */
export const zGetV1ServiceAgreementsWithCustomerResponse = zPaginatedServiceAgreementListBody;

export const zGetV1ServiceAgreementsWithProviderQuery = z.object({
    page: z.number().int().optional(),
    limit: z.number().int().optional(),
    'filter[providerBusinessPartnerId]': z.record(z.unknown()).optional(),
    'filter[customer.name]': z.record(z.unknown()).optional(),
    'filter[serviceAgreementStatus]': z.record(z.unknown()).optional()
});

/**
 * Successful Response
 */
export const zGetV1ServiceAgreementsWithProviderResponse = zPaginatedServiceAgreementListBody;
